'use client';

import { useSyncExternalStore } from 'react';
export const MOBILE_BREAKPOINT = 768;

/**
 * Pure non-React helper: returns true when the supplied (or current) viewport
 * width is below the `md` breakpoint. SSR-safe — returns `false` when no
 * width is supplied and `window` is unavailable.
 */
export function isMobileViewportWidth(width?: number): boolean {
  if (width != null) {
    return width < MOBILE_BREAKPOINT;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
}

/** Client snapshot for `useSyncExternalStore` — reads the current mobile media query state. */
function getSnapshot(): boolean {
  return globalThis.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
}

/** SSR snapshot for `useSyncExternalStore` — returns undefined so hydration defers to the client. */
function getServerSnapshot(): boolean | undefined {
  return undefined;
}

/** Subscribes to mobile breakpoint media query changes for `useSyncExternalStore`. */
function subscribe(callback: () => void): () => void {
  const mediaQuery = globalThis.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
  mediaQuery.addEventListener('change', callback);
  return () => mediaQuery.removeEventListener('change', callback);
}

/**
 * Returns true when the viewport is narrower than the `md` breakpoint (768px).
 * Returns `undefined` during SSR / before hydration.
 */
export function useIsMobile() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}